import { cn } from '@/utils/utils.ts';
import { useGetPlantOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { PlantProductionAndStorageFlowAndDetailsCard } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-flow-and-details-card.tsx';
import { DeviceTreeTableCard } from '@/features/devices/components/device-tree-table/device-tree-table-card.tsx';
import { useGetPlantDevicesOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview.ts';
import { PlantProductionAndStorageEnergyManagement } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-energy-management.tsx';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';
import { PlantProductionAndStorageTodayMetrics } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-today-metrics.tsx';
import { getTodayDateRange } from '@/utils/get-today-date-range.ts';
import { PlantEvents } from '@/features/plants/components/plant-events/plant-events.tsx';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';

import style from '@/features/plants/components/plant-overview/production-and-storage/advanced/plant-production-and-storage-overview-advanced.module.scss';
import { MultiDeviceMetricsTableCard } from '@/features/devices/components/multi-device-metrics-table/multi-device-metrics-table-card.tsx';
import { usePermissions } from '@/hooks/use-permission.ts';

export const PlantProductionAndStorageOverviewAdvanced = ({
  plantId,
}: {
  plantId: string | undefined;
}) => {
  const [canEditProductionFields, canEditStorageFields] = usePermissions<'plants'>([
    ['plants', 'edit.plant.production-fields'],
    ['plants', 'edit.plant.storage-fields'],
  ]);

  const { data: plant, isLoading: isPlantLoading } = useGetPlantQuery({
    plantId: plantId,
  });

  const { data: overview, isLoading: isPlantOverviewLoading } = useGetPlantOverviewQuery({
    plantId: plantId,
  });

  const { data: devicesOverview, isLoading: isDevicesOverviewLoading } =
    useGetPlantDevicesOverviewQuery({
      plantId: plant?.plantId,
      enabled: Boolean(plant?.plantId),
      dateRange: getTodayDateRange(),
    });

  const { data: plantDevices, isLoading: isDevicesLoading } = useGetPlantDevicesQuery({
    plantId: plant?.plantId ?? '',
    enabled: Boolean(plant?.plantId),
  });

  const isLoading = isPlantLoading || isPlantOverviewLoading;

  return (
    <div className={cn(style.grid_container)}>
      <div className={cn(style.grid, 'gap-6')}>
        <PlantProductionAndStorageTodayMetrics
          overview={overview}
          isLoading={isLoading}
          className={style.today_metrics}
        />

        <PlantProductionAndStorageFlowAndDetailsCard
          overview={overview}
          plant={plant}
          isLoading={isLoading}
          className={cn(style.realtime_metrics, 'min-h-[490px]')}
        />

        <PlantProductionAndStorageEnergyManagement
          isLoading={isLoading}
          plantId={plant?.plantId}
          className={cn(style.energy_management_chart, 'min-h-[440px] sm:min-h-[535px]')}
        />

        <DeviceTreeTableCard
          title="Device"
          data={plantDevices ?? []}
          isLoading={isLoading || isDevicesLoading}
          className={cn(style.devices)}
        />
        <MultiDeviceMetricsTableCard
          data={devicesOverview?.production}
          config={devicesOverview?.config.production}
          type={'production'}
          isLoading={isLoading || isDevicesOverviewLoading}
          plantId={plant?.plantId || ''}
          title="Overview Production"
          className={style.production}
          showSelectFields={canEditProductionFields}
        />
        <MultiDeviceMetricsTableCard
          data={devicesOverview?.storage}
          config={devicesOverview?.config?.storage}
          type={'storage'}
          isLoading={isLoading || isDevicesOverviewLoading}
          plantId={plant?.plantId || ''}
          title="Overview Battery"
          className={style.battery}
          showSelectFields={canEditStorageFields}
        />
        <PlantEvents className={style.events} />
      </div>
    </div>
  );
};
